import { useEffect, useState } from "react";
import { useAppServices } from "../../../hook/services";
import { Icon } from "@iconify/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const ARTICLES_PER_PAGE = 10;

const hl = (str) => (typeof str === "string" ? str.replace(/HighLevel/gi, "CRM") : str);

const ArticleDetailModal = ({ article, onClose }) => {
  if (!article) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content radius-12">
          <div className="modal-header border-bottom px-4 py-3">
            <div>
              <h5 className="modal-title fw-semibold text-primary-light mb-1">
                {hl(article.title)}
              </h5>
              <div className="d-flex gap-2 flex-wrap">
                {article.category && (
                  <span className="badge bg-primary-subtle text-primary fw-medium px-2 py-1">
                    <Icon icon="solar:folder-bold" className="me-1" />
                    {hl(article.category)}
                  </span>
                )}
                {article.folder && (
                  <span className="badge bg-info-subtle text-info fw-medium px-2 py-1">
                    <Icon icon="solar:folder-open-bold" className="me-1" />
                    {hl(article.folder)}
                  </span>
                )}
                {article.lastUpdated && (
                  <span className="badge bg-light text-secondary fw-medium px-2 py-1">
                    <Icon icon="solar:calendar-bold" className="me-1" />
                    {hl(article.lastUpdated)}
                  </span>
                )}
              </div>
            </div>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body px-4 py-3">
            {article.markdown ? (
              <div className="article-markdown-body">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => <h2 className="fw-bold mt-4 mb-3 text-primary-light" style={{ fontSize: "1.5rem", borderBottom: "2px solid #e9ecef", paddingBottom: "0.4rem" }}>{children}</h2>,
                    h2: ({ children }) => <h3 className="fw-semibold mt-4 mb-2 text-primary-light" style={{ fontSize: "1.2rem" }}>{children}</h3>,
                    h3: ({ children }) => <h4 className="fw-semibold mt-3 mb-2 text-secondary-light" style={{ fontSize: "1.05rem" }}>{children}</h4>,
                    h4: ({ children }) => <h5 className="fw-semibold mt-3 mb-2" style={{ fontSize: "0.97rem" }}>{children}</h5>,
                    p: ({ children }) => <p className="mb-3" style={{ lineHeight: "1.75", color: "#344054" }}>{children}</p>,
                    a: ({ href, children }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary fw-medium" style={{ textDecoration: "underline" }}>
                        {children}
                      </a>
                    ),
                    ul: ({ children }) => <ul className="mb-3 ps-4" style={{ lineHeight: "1.8", color: "#344054" }}>{children}</ul>,
                    ol: ({ children }) => <ol className="mb-3 ps-4" style={{ lineHeight: "1.8", color: "#344054" }}>{children}</ol>,
                    li: ({ children }) => <li className="mb-1">{children}</li>,
                    blockquote: ({ children }) => (
                      <blockquote className="border-start border-4 border-primary ps-3 py-1 my-3 bg-light rounded-end" style={{ color: "#555" }}>
                        {children}
                      </blockquote>
                    ),
                    code: ({ inline, children }) =>
                      inline ? (
                        <code className="px-1 py-0 rounded" style={{ background: "#f0f2f5", color: "#d63384", fontSize: "0.88em", fontFamily: "monospace" }}>
                          {children}
                        </code>
                      ) : (
                        <pre className="rounded-3 p-3 my-3" style={{ background: "#f8f9fa", border: "1px solid #e9ecef", overflowX: "auto", fontSize: "0.87rem", lineHeight: "1.6" }}>
                          <code>{children}</code>
                        </pre>
                      ),
                    table: ({ children }) => (
                      <div className="table-responsive my-3">
                        <table className="table table-bordered table-sm" style={{ fontSize: "0.9rem" }}>
                          {children}
                        </table>
                      </div>
                    ),
                    thead: ({ children }) => <thead className="table-light fw-semibold">{children}</thead>,
                    tbody: ({ children }) => <tbody>{children}</tbody>,
                    tr: ({ children }) => <tr>{children}</tr>,
                    th: ({ children }) => <th className="px-3 py-2">{children}</th>,
                    td: ({ children }) => <td className="px-3 py-2">{children}</td>,
                    hr: () => <hr className="my-4 border-secondary-subtle" />,
                    strong: ({ children }) => <strong className="fw-semibold">{children}</strong>,
                    img: ({ src, alt }) => (
                      <img src={src} alt={alt} className="img-fluid rounded-3 my-2" style={{ maxWidth: "100%" }} />
                    ),
                  }}
                >
                  {hl(article.markdown)}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="text-secondary">No content available.</p>
            )}
          </div>
          <div className="modal-footer px-4 py-3 border-top">
            {article.url && (
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary btn-sm radius-8"
              >
                <Icon icon="solar:link-bold" className="me-1" />
                View Original
              </a>
            )}
            <button className="btn btn-primary btn-sm radius-8" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Articles = () => {
  const AppService = useAppServices();

  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedFolder, setSelectedFolder] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loadingArticle, setLoadingArticle] = useState(false);

  const fetchArticles = async (page = 1, s = search, cat = selectedCategory, fol = selectedFolder) => {
    setLoading(true);
    const parts = [`page=${page}`, `limit=${ARTICLES_PER_PAGE}`];
    if (s) parts.push(`search=${encodeURIComponent(s)}`);
    if (cat) parts.push(`category=${encodeURIComponent(cat)}`);
    if (fol) parts.push(`folder=${encodeURIComponent(fol)}`);
    const { response } = await AppService.ghl_article.get({ query: parts.join("&") });
    setArticles(response?.data || []);
    setTotal(response?.total || 0);
    setTotalPages(response?.totalPages || 1);
    setCurrentPage(page);
    setLoading(false);
  };

  const fetchCategories = async () => {
    const { response } = await AppService.ghl_article.getCategories({});
    setCategories(response?.data || []);
  };

  const fetchFolders = async (cat) => {
    const query = cat ? `category=${encodeURIComponent(cat)}` : "";
    const { response } = await AppService.ghl_article.getFolders({ query });
    setFolders(response?.data || []);
  };

  useEffect(() => {
    fetchCategories();
    fetchArticles(1);
  }, []);

  const handleSearchChange = (val) => {
    setSearch(val);
    fetchArticles(1, val, selectedCategory, selectedFolder);
  };

  const handleCategoryChange = (val) => {
    setSelectedCategory(val);
    setSelectedFolder("");
    fetchFolders(val);
    fetchArticles(1, search, val, "");
  };

  const handleFolderChange = (val) => {
    setSelectedFolder(val);
    fetchArticles(1, search, selectedCategory, val);
  };

  const handleClearSearch = () => {
    setSearch("");
    setSelectedCategory("");
    setSelectedFolder("");
    fetchFolders("");
    fetchArticles(1, "", "", "");
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchArticles(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openArticle = async (id) => {
    setLoadingArticle(true);
    setSelectedArticle({ _id: id, title: "Loading…" });
    const { response } = await AppService.ghl_article.getOne({ id });
    setSelectedArticle(response?.data || null);
    setLoadingArticle(false);
  };

  const handleSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    const { response } = await AppService.ghl_article.seed({});
    setSeedResult(response?.message || "Done");
    fetchArticles(1);
    fetchCategories();
    setSeeding(false);
  };

  const getCategoryColor = (category) => {
    const colors = ["primary", "success", "info", "warning", "danger", "secondary"];
    const idx =
      Math.abs((category || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0)) %
      colors.length;
    return colors[idx];
  };

  const buildPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      const near = Math.abs(i - currentPage) <= 2;
      const edge = i === 1 || i === totalPages;
      if (near || edge) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...");
      }
    }
    return pages;
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="fw-semibold text-primary-light mb-1">GHL Articles</h4>
          <p className="text-secondary-light mb-0 fs-6">
            Browse and search through all GoHighLevel help articles
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {seedResult && (
            <span className="badge bg-success-subtle text-success px-3 py-2 radius-8 fw-medium">
              <Icon icon="solar:check-circle-bold" className="me-1" />
              {seedResult}
            </span>
          )}
          <button
            className="btn btn-outline-primary radius-8 d-flex align-items-center gap-2"
            onClick={handleSeed}
            disabled={seeding}
          >
            {seeding ? (
              <>
                <span className="spinner-border spinner-border-sm" />
                Seeding…
              </>
            ) : (
              <>
                <Icon icon="solar:database-bold" width={16} />
                Seed DB
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card radius-12 border-0 shadow-sm mb-4">
        <div className="card-body px-4 py-3">
          <div className="row g-3 align-items-end">
            <div className="col-md-5">
              <label className="form-label fw-medium text-secondary-light mb-1">Search</label>
              <div className="input-group">
                <span className="input-group-text bg-transparent border-end-0">
                  <Icon icon="solar:magnifer-bold" className="text-secondary" />
                </span>
                <input
                  type="text"
                  className="form-control radius-8 border-start-0 ps-0"
                  placeholder="Search by title, category, folder…"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                {search && (
                  <button
                    className="btn btn-outline-secondary border-start-0"
                    onClick={() => handleSearchChange("")}
                    type="button"
                  >
                    <Icon icon="solar:close-circle-bold" />
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-medium text-secondary-light mb-1">Category</label>
              <select
                className="form-select radius-8"
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-medium text-secondary-light mb-1">Folder</label>
              <select
                className="form-select radius-8"
                value={selectedFolder}
                onChange={(e) => handleFolderChange(e.target.value)}
                disabled={!selectedCategory}
              >
                <option value="">All Folders</option>
                {folders.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div className="col-md-1">
              <button
                className="btn btn-light radius-8 w-100"
                onClick={handleClearSearch}
                title="Clear filters"
              >
                <Icon icon="solar:refresh-bold" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <p className="text-secondary-light mb-0 fs-6">
          {loading ? (
            <span className="placeholder-glow"><span className="placeholder col-4" /></span>
          ) : total === 0 ? (
            "No articles found"
          ) : (
            <>
              Showing{" "}
              <strong>
                {(currentPage - 1) * ARTICLES_PER_PAGE + 1}–{Math.min(currentPage * ARTICLES_PER_PAGE, total)}
              </strong>{" "}
              of <strong>{total}</strong> articles
            </>
          )}
        </p>
        {totalPages > 1 && (
          <small className="text-secondary-light">Page {currentPage} of {totalPages}</small>
        )}
      </div>

      {/* Articles List */}
      {loading ? (
        <div className="card radius-12 border-0 shadow-sm">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="card-body border-bottom py-3 px-4 placeholder-glow">
              <div className="d-flex align-items-start gap-3">
                <span className="placeholder bg-secondary rounded-3 flex-shrink-0" style={{ width: 42, height: 42 }} />
                <div className="flex-grow-1">
                  <div className="placeholder col-7 mb-2 d-block" />
                  <div className="placeholder col-4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="card radius-12 border-0 shadow-sm text-center py-5">
          <Icon icon="solar:documents-bold" width={48} className="text-secondary mb-3 mx-auto d-block" />
          <h6 className="fw-semibold text-secondary-light">No articles found</h6>
          <p className="text-secondary mb-3 fs-6">Try adjusting your filters, or seed the database first.</p>
          <button className="btn btn-primary radius-8 mx-auto px-4" onClick={handleSeed} disabled={seeding}>
            <Icon icon="solar:database-bold" className="me-2" />
            Seed Articles
          </button>
        </div>
      ) : (
        <div className="card radius-12 border-0 shadow-sm overflow-hidden">
          {articles.map((article, idx) => {
            const color = getCategoryColor(article.category);
            return (
              <div
                key={article._id}
                className={`d-flex align-items-start gap-3 px-4 py-3 ${idx < articles.length - 1 ? "border-bottom" : ""}`}
                style={{ cursor: "pointer", transition: "background 0.15s" }}
                onClick={() => openArticle(article._id)}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8f9fb")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "")}
              >
                <div
                  className={`d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 bg-${color}-subtle`}
                  style={{ width: 42, height: 42 }}
                >
                  <Icon icon="solar:document-text-bold" className={`text-${color}`} width={20} />
                </div>
                <div className="flex-grow-1 min-width-0">
                  <h6 className="fw-semibold text-primary-light mb-1 text-truncate">{hl(article.title)}</h6>
                  <div className="d-flex gap-2 flex-wrap align-items-center">
                    {article.category && (
                      <span className={`badge bg-${color}-subtle text-${color} fw-medium px-2`} style={{ fontSize: "0.73rem" }}>
                        {hl(article.category)}
                      </span>
                    )}
                    {article.folder && (
                      <span className="badge bg-light text-secondary fw-medium px-2" style={{ fontSize: "0.73rem" }}>
                        <Icon icon="solar:folder-open-linear" className="me-1" style={{ fontSize: "0.75rem" }} />
                        {hl(article.folder)}
                      </span>
                    )}
                    {article.lastUpdated && (
                      <span className="text-secondary" style={{ fontSize: "0.75rem" }}>
                        <Icon icon="solar:calendar-linear" className="me-1" style={{ fontSize: "0.75rem" }} />
                        {hl(article.lastUpdated)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center text-secondary flex-shrink-0">
                  <Icon icon="solar:arrow-right-linear" width={18} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="d-flex justify-content-center mt-4 gap-1 flex-wrap">
          <button
            className="btn btn-light btn-sm radius-8 px-3"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <Icon icon="solar:arrow-left-linear" />
          </button>

          {buildPageNumbers().map((n, i) =>
            n === "..." ? (
              <span key={`e-${i}`} className="btn btn-light btn-sm radius-8 px-3 disabled pe-none">…</span>
            ) : (
              <button
                key={n}
                className={`btn btn-sm radius-8 px-3 ${currentPage === n ? "btn-primary" : "btn-light"}`}
                onClick={() => handlePageChange(n)}
              >
                {n}
              </button>
            )
          )}

          <button
            className="btn btn-light btn-sm radius-8 px-3"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <Icon icon="solar:arrow-right-linear" />
          </button>
        </div>
      )}

      {/* Article Detail Modal */}
      {selectedArticle && (
        <ArticleDetailModal
          article={loadingArticle ? null : selectedArticle}
          onClose={() => setSelectedArticle(null)}
        />
      )}
    </div>
  );
};

export default Articles;
